import React, { useEffect, useState } from 'react';
import {
    Alert,
    Autocomplete,
    Button,
    Group,
    Modal,
    NumberInput,
    Select,
    Stack,
    Text,
    TextInput,
} from '@mantine/core';
import { Info, Warning } from '@phosphor-icons/react';

import { getMerchantId } from '@api/auth';
import { getOrgConfig, type MeasurementBasis } from '@api/organization';
import { readApiError } from '@utils/apiError';
import {
    COMMON_GST_RATES,
    validateGstRate,
    validateHsnCode,
} from '@utils/tax';
import {
    createProduct,
    getProductCategories,
    getProductGroups,
    getProductTypes,
} from '@api/products';

interface AddProductModalProps {
    opened: boolean;
    onClose: () => void;
    /** Fired after a successful create so the grid can reload. */
    onCreated: () => void;
}

/**
 * Combo/bundle product types are excluded from the picker. Selecting one without
 * also authoring `combo_product_mappings` rows yields a half-defined combo that
 * downstream BOM and fulfilment reads treat as broken.
 */
const COMBO_TYPE_PATTERN = /bundle|combo|kit/i;

const AddProductModal: React.FC<AddProductModalProps> = ({
    opened,
    onClose,
    onCreated,
}) => {
    const [sku, setSku] = useState('');
    const [name, setName] = useState('');
    const [category, setCategory] = useState('');
    const [subCategory, setSubCategory] = useState('');
    const [productGroup, setProductGroup] = useState('');
    const [productType, setProductType] = useState<string | null>(null);
    const [price, setPrice] = useState<string | number>('');
    const [weightGrams, setWeightGrams] = useState<string | number>('');
    const [volumeCbm, setVolumeCbm] = useState<string | number>('');
    const [gstRate, setGstRate] = useState<string>('');
    const [hsnCode, setHsnCode] = useState('');
    const [unitsPerCase, setUnitsPerCase] = useState<string | number>('');

    const [categories, setCategories] = useState<string[]>([]);
    const [groups, setGroups] = useState<string[]>([]);
    const [types, setTypes] = useState<string[]>([]);
    const [basis, setBasis] = useState<MeasurementBasis>('WEIGHT');

    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
    const [saveError, setSaveError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    const reset = () => {
        setSku('');
        setName('');
        setCategory('');
        setSubCategory('');
        setProductGroup('');
        setProductType(null);
        setPrice('');
        setWeightGrams('');
        setVolumeCbm('');
        setGstRate('');
        setHsnCode('');
        setFieldErrors({});
        setSaveError(null);
        setSaving(false);
    };

    const handleClose = () => {
        reset();
        onClose();
    };

    // Load the pickers and the org's measurement basis when the modal opens.
    useEffect(() => {
        if (!opened) return;

        void Promise.all([
            getProductCategories().catch(() => [] as string[]),
            getProductGroups().catch(() => [] as string[]),
            getProductTypes().catch(() => [] as string[]),
        ]).then(([cats, grps, tps]) => {
            setCategories(cats);
            setGroups(grps);
            setTypes(tps.filter((t) => t && !COMBO_TYPE_PATTERN.test(t)));
        });

        void getMerchantId()
            .then((merchantId) => (merchantId ? getOrgConfig(merchantId) : null))
            .then((org) => setBasis(org?.measurementBasis ?? 'WEIGHT'))
            .catch(() => setBasis('WEIGHT'));
    }, [opened]);

    const showWeight = basis === 'WEIGHT' || basis === 'BOTH';
    const showVolume = basis === 'VOLUME' || basis === 'BOTH';

    const validate = (): boolean => {
        const errors: Record<string, string> = {};
        if (!sku.trim()) errors.sku = 'SKU is required';
        if (!name.trim()) errors.name = 'Name is required';
        if (!category.trim()) errors.category = 'Category is required';
        const gstError = validateGstRate(gstRate);
        if (gstError) errors.gstRate = gstError;

        const hsnError = validateHsnCode(hsnCode);
        if (hsnError) errors.hsnCode = hsnError;

        setFieldErrors(errors);
        return Object.keys(errors).length === 0;
    };

    /** Blank stays undefined so we never persist a Postgres NaN into a numeric column. */
    const toNumber = (value: string | number): number | undefined => {
        if (value === '' || value === null || value === undefined) return undefined;
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : undefined;
    };

    const handleSubmit = async () => {
        if (!validate()) return;

        setSaving(true);
        setSaveError(null);
        try {
            await createProduct({
                sku: sku.trim(),
                name: name.trim(),
                category: category.trim(),
                subCategory: subCategory.trim() || undefined,
                productGroup: productGroup.trim() || undefined,
                productType: productType ?? undefined,
                price: toNumber(price),
                weightGrams: showWeight ? toNumber(weightGrams) : undefined,
                volumeCbm: showVolume ? toNumber(volumeCbm) : undefined,
                gstRate: toNumber(gstRate),
                hsnCode: hsnCode.trim() || undefined,
                unitsPerCase: toNumber(unitsPerCase),
            });
            onCreated();
            handleClose();
        } catch (err) {
            setSaveError(
                readApiError(err, 'Could not add the product. Please try again.'),
            );
            setSaving(false);
        }
    };

    return (
        <Modal
            opened={opened}
            onClose={handleClose}
            title="Add product"
            size="md"
            centered
        >
            <Stack gap="md">
                <TextInput
                    label="SKU"
                    description="Also used as the product ID. Must be unique."
                    placeholder="e.g. BSL-3C-LTE-BNT-D-003"
                    value={sku}
                    onChange={(e) => setSku(e.currentTarget.value)}
                    error={fieldErrors.sku}
                    withAsterisk
                    data-autofocus
                />

                <TextInput
                    label="Name"
                    placeholder="e.g. Bento Lite 3C Web Verse"
                    value={name}
                    onChange={(e) => setName(e.currentTarget.value)}
                    error={fieldErrors.name}
                    withAsterisk
                />

                <Autocomplete
                    label="Category"
                    placeholder="Pick one or type a new category"
                    data={categories}
                    value={category}
                    onChange={setCategory}
                    error={fieldErrors.category}
                    withAsterisk
                />

                <Group grow align="flex-start">
                    <Autocomplete
                        label="Product group"
                        placeholder="Optional"
                        data={groups}
                        value={productGroup}
                        onChange={setProductGroup}
                    />
                    <Select
                        label="Type"
                        placeholder="Optional"
                        data={types}
                        value={productType}
                        onChange={setProductType}
                        clearable
                    />
                </Group>

                <Group grow align="flex-start">
                    <TextInput
                        label="Sub-category"
                        placeholder="Optional"
                        value={subCategory}
                        onChange={(e) => setSubCategory(e.currentTarget.value)}
                    />
                    <NumberInput
                        label="Price"
                        placeholder="Optional"
                        value={price}
                        onChange={setPrice}
                        min={0}
                        decimalScale={2}
                    />
                </Group>

                {showWeight && (
                    <NumberInput
                        label="Weight (g)"
                        placeholder="Optional"
                        value={weightGrams}
                        onChange={setWeightGrams}
                        min={0}
                    />
                )}

                {showVolume && (
                    <NumberInput
                        label="Volume (cbm)"
                        placeholder="Optional"
                        value={volumeCbm}
                        onChange={setVolumeCbm}
                        min={0}
                        decimalScale={4}
                    />
                )}

                <NumberInput
                    label="Units per case"
                    description="How many go in one shipping case. Blank means the product is not packed in cases."
                    placeholder="Optional"
                    value={unitsPerCase}
                    onChange={setUnitsPerCase}
                    min={1}
                    allowDecimal={false}
                />

                <Group grow align="flex-start">
                    <Autocomplete
                        label="GST %"
                        description="Leave blank if unknown — blank is not 0%"
                        placeholder="e.g. 18"
                        data={COMMON_GST_RATES}
                        value={gstRate}
                        onChange={setGstRate}
                        error={fieldErrors.gstRate}
                    />
                    <TextInput
                        label="HSN/SAC code"
                        placeholder="Optional"
                        value={hsnCode}
                        onChange={(e) => setHsnCode(e.currentTarget.value)}
                        error={fieldErrors.hsnCode}
                    />
                </Group>

                <Alert color="blue" icon={<Info size={16} />}>
                    <Text size="xs">
                        This adds the product to your catalog. It won&apos;t show stock
                        or channel inventory until it&apos;s mapped to a channel and a
                        feed reports it.
                    </Text>
                </Alert>

                {saveError && (
                    <Alert color="red" icon={<Warning size={16} />}>
                        {saveError}
                    </Alert>
                )}

                <Group justify="flex-end" mt="xs">
                    <Button variant="subtle" color="gray" onClick={handleClose}>
                        Cancel
                    </Button>
                    <Button onClick={handleSubmit} loading={saving}>
                        Add product
                    </Button>
                </Group>
            </Stack>
        </Modal>
    );
};

export default AddProductModal;
